import React, { useState, useEffect } from 'react';
import { useNotification } from 'config/Notification';
import { Button, Form } from 'react-bootstrap';
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
} from '@mui/material';
import Warning from '@mui/icons-material/Warning';
import UploadIcon from '@mui/icons-material/Upload';

import { fetchPPTReports as fetchPPTReportsService, updatePDFReports as updatePDFReportsService } from 'services/modules/vapt/report';
import { useSelector } from 'react-redux';

function UploadPDFPage() {
    // State variables
    const [fileError, setFileError] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [reportsData, setReportsData] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedFile, setSelectedFile] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [totalResults, setTotalResults] = useState(0);

    const vaptDimensionsContainer = useSelector((state) => state.vaptData.vaptDimensions);
    // Notification
    const { openNotification } = useNotification();

    useEffect(() => {
        fetchReportsData();
        // eslint-disable-next-line
    }, [page, rowsPerPage, searchQuery,]);

    // Handles fetching the reports data
    const fetchReportsData = async () => {
        setLoading(true);
        const params = {
            page: page + 1,
            limit: rowsPerPage,
            sortBy: '-createdAt',
            reportStatus: 'COMPLETED'
        };
        if (searchQuery.trim() !== '') {
            params.search = searchQuery.trim();
        }
        try {
            let response = await fetchPPTReportsService(params);
            setTotalResults(response?.data.totalResults);
            if (response?.status === 200)
                setReportsData(response?.data)
            else
                openNotification('Error while fetching report data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handles page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handles change in rows per page
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Method to change the file before uploading

    const handleFileChange = (event, rowId) => {
        const file = event.target.files[0];

        if (file) {
            const validExtensions = [".pdf"];
            const fileExtension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();

            if (validExtensions.includes(`.${fileExtension}`)) {
                if (file.size <= 6 * 1024 * 1024) {
                    setSelectedFile((prevFiles) => ({
                        ...prevFiles,
                        [rowId]: file,
                    }));
                    setFileError((prevErrors) => ({
                        ...prevErrors,
                        [rowId]: null,
                    }));
                } else {
                    setSelectedFile((prevFiles) => ({
                        ...prevFiles,
                        [rowId]: null,
                    }));
                    setFileError((prevErrors) => ({
                        ...prevErrors,
                        [rowId]: "File size exceeds the maximum limit of 6MB",
                    }));
                }
            } else {
                setSelectedFile((prevFiles) => ({
                    ...prevFiles,
                    [rowId]: null,
                }));
                setFileError((prevErrors) => ({
                    ...prevErrors,
                    [rowId]: "Please choose a valid PDF file",
                }));
            }
        } else {
            setSelectedFile((prevFiles) => ({
                ...prevFiles,
                [rowId]: null,
            }));
            setFileError((prevErrors) => ({
                ...prevErrors,
                [rowId]: "Please choose a valid PDF file",
            }));
        }
    }

    // This method handles to submit the file
    const handleFileSubmit = async (reportId) => {
        if (selectedFile) {
            try {
                setLoading(true);
                const formData = new FormData();
                const file = Object.values(selectedFile)[0]; 
                formData.append('data', file);
                let response = await updatePDFReportsService({ reportId, formData });
                if (response.status === 200) {
                    openNotification('File uploaded sucessfully', 'success');
                }
                else {
                    openNotification('Error while uploading file', 'error');
                }
                fetchReportsData();
            } catch (error) {
                openNotification('Error submitting file:', 'error')
            } finally {
                setLoading(false);
            }
        }
        else {
            openNotification('Invalid file selected.', 'error')
        }
    };

    // Method handles to get the category name based on id
    const getCategoryName = (catId) => {
        const category = vaptDimensionsContainer?.find(cat => cat._id === catId);
        return category?.label;
    };

    // Handles date formatting
    const formatDate = (date) => {
        const formattedDate = new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
        });
        return formattedDate;
    };

    // Handles time formatting
    const formatTime = (date) => {
        return new Date(date).toLocaleTimeString();
    };
    return (
        <div className='card-container'>
            <Card className='card card-width' >
                <CardHeader
                    title={
                        <React.Fragment>
                            <div className='card-head'>
                                <label className='label'> Upload PDF Reports</label>
                            </div>
                        </React.Fragment>
                    }
                />
                <Divider className='divider' />
                <div className='flex-card'>
                    <div className='m-2'>
                        <TextField
                            label="Search"
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead className="table-head">
                                <TableRow>
                                    <TableCell>Report Id</TableCell>
                                    <TableCell>Category </TableCell>
                                    <TableCell>SubCategory </TableCell>
                                    <TableCell>Domain URL</TableCell>
                                    <TableCell>Requested On</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Upload</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : reportsData?.results?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Warning color="black" fontSize="small" />
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                ) : reportsData?.results?.map((result) => (
                                    <React.Fragment key={result.id}>
                                        <TableRow>
                                            <TableCell className="name-table"> {result.reportId}</TableCell>
                                            <TableCell className='text-bold text-black'>{getCategoryName(result.category)}</TableCell>
                                            <TableCell className="text-bold text-black">{result?.subcategory}</TableCell>
                                            <TableCell className="text-secondary">{result.domain}</TableCell>
                                            <TableCell>
                                                <div className="date-formatter">
                                                    <p className="text-bold text-black m-0">{formatDate(result.createdAt)}</p>
                                                    <p className="text-primary subDim-text m-0" >{formatTime(result.createdAt)}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-bold text-black m-0">{result.reportStatus}</p>
                                            </TableCell>
                                            <TableCell>
                                                <Form.Group >
                                                    <input type="file"
                                                        accept=".pdf"
                                                        onChange={(e) => handleFileChange(e, result.id)}
                                                        className={`form-control-file ${fileError[result.id] ? "is-invalid" : ""}`}
                                                    />
                                                    {fileError[result.id] && <div className="invalid-feedback">{fileError[result.id]}</div>}
                                                </Form.Group>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleFileSubmit(result.reportId)}
                                                >
                                                    <UploadIcon /> Submit file
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component={Paper}
                        rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
                        count={totalResults || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

export default UploadPDFPage;